import React, { useState, useEffect } from 'react';
import UserLayout from '../components/UserLayout';
import './CommandCentrePage.css';
import { 
  ShoppingBag,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Activity, 
  Zap, 
  Calendar, 
  ChevronDown,
  LayoutGrid,
  Bell,
  Package,
  HeartPulse,
  Megaphone,
  Layers,
  Sparkles,
  ArrowUpRight
} from 'lucide-react';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { apiClient } from '../api/api';

const CommandCentrePage = () => {
    const [loading, setLoading] = useState(true);
    const { user, dateRange } = useAuth();
    const { theme } = useTheme();
    const isLight = theme === 'light';
    const [brands, setBrands] = useState([]);
    const [performanceData, setPerformanceData] = useState({
        gmv: { total: '$0', growth: '0%', topBrand: 'N/A', topBrandVal: '$0', activeBrands: '0' },
        adSpend: { total: '$0', growth: '0%', avgRoi: '0x', totalRevenue: '$0' },
        orders: { total: '0', growth: '0%', aov: '$0', cvr: '0%' }
    });
    const [intelligenceInsights, setIntelligenceInsights] = useState([]);

    const [alerts, setAlerts] = useState([
        { id: 1, label: 'Policy Violations', count: 0, items: [], type: 'danger' },
        { id: 2, label: 'New Campaigns Avail.', count: 0, items: [], type: 'info' },
        { id: 3, label: 'Shop Health', count: 0, items: [], type: 'warning' },
        { id: 4, label: 'Low Inventory', count: 0, items: [], type: 'inventory' },
        { id: 5, label: 'Promotions Expiring', count: 0, items: [], type: 'promo' },
        { id: 6, label: 'CRM Campaigns Completed', count: 0, items: [], type: 'crm' }
    ]);

    useEffect(() => {
        const fetchData = async () => {
            if (!user) return;
            setLoading(true);
            try {
                const resp = await apiClient.get(`/api/command-centre/roster?range=${dateRange}`);
                if (resp.data?.brands) {
                    setBrands(resp.data.brands);
                    
                    if (resp.data.insights) {
                        setIntelligenceInsights(resp.data.insights);
                    }

                    // Helper to clear currency symbols for summing
                    const n = (val) => {
                        if (typeof val === 'number') return val;
                        return parseFloat(String(val || '0').replace(/[$,]/g, '')) || 0;
                    };

                    // Aggregate calculation for all dashboard metrics
                    const totalGmv = resp.data.brands.reduce((sum, b) => sum + n(b.gmv), 0);
                    const totalOrders = resp.data.brands.reduce((sum, b) => sum + n(b.orders), 0);
                    const totalSpend = resp.data.brands.reduce((sum, b) => sum + n(b.adSpend), 0);
                    const activeBrandsCount = resp.data.brands.length;
                    
                    let topBrandName = 'N/A';
                    let topBrandV = 0;
                    resp.data.brands.forEach(b => {
                        const val = n(b.gmv);
                        if (val > topBrandV) {
                            topBrandV = val;
                            topBrandName = b.name;
                        }
                    });

                    // Compute portfolio-level growth as GMV-weighted avg of per-brand growth
                    const parseGrowthNum = (g) => parseFloat((g || '0').replace('+','').replace('%','')) || 0;
                    const gmvWeightedGrowth = (() => {
                        if (totalGmv === 0) return '+0%';
                        const ws = resp.data.brands.reduce((s, b) => s + n(b.gmv) * parseGrowthNum(b.gmvGrowth), 0);
                        const avg = ws / totalGmv;
                        return (avg >= 0 ? '+' : '') + avg.toFixed(1) + '%';
                    })();
                    const ordersWeightedGrowth = (() => {
                        if (totalOrders === 0) return '+0%';
                        const ws = resp.data.brands.reduce((s, b) => s + n(b.orders) * parseGrowthNum(b.ordersGrowth), 0);
                        const avg = ws / totalOrders;
                        return (avg >= 0 ? '+' : '') + avg.toFixed(1) + '%';
                    })();
                    const totalVisitors = resp.data.brands.reduce((sum, b) => sum + n(b.visitors), 0);
                    const cvrPct = totalVisitors > 0 ? ((totalOrders / totalVisitors) * 100).toFixed(1) + '%' : '0%';

                    setPerformanceData({
                        gmv: {
                            total: `$${totalGmv.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                            growth: gmvWeightedGrowth,
                            topBrand: topBrandName,
                            topBrandVal: `$${topBrandV.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                            activeBrands: activeBrandsCount.toString()
                        },
                        adSpend: {
                            total: `$${totalSpend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                            growth: '—',
                            avgRoi: totalSpend > 0 ? (totalGmv / totalSpend).toFixed(2) + 'x' : '0x',
                            totalRevenue: `$${totalGmv.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        },
                        orders: {
                            total: totalOrders.toLocaleString(),
                            growth: ordersWeightedGrowth,
                            aov: totalOrders > 0 ? `$${(totalGmv / totalOrders).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00',
                            cvr: cvrPct
                        }
                    });
                    // Aggregate Alerts & Triage Data
                    const violationBrands = resp.data.brands.filter(b => (b.violations || 0) > 0);
                    const totalViolations = violationBrands.reduce((sum, b) => sum + (b.violations || 0), 0);
                    
                    const availableCampaignBrands = resp.data.brands.filter(b => (b.availableCampaignsCount || 0) > 0);
                    const totalAvailableCampaigns = availableCampaignBrands.reduce((sum, b) => sum + (b.availableCampaignsCount || 0), 0);

                    const lowStockBrands = resp.data.brands.filter(b => (b.lowStockCount || 0) > 0);
                    const totalLowStock = lowStockBrands.reduce((sum, b) => sum + (b.lowStockCount || 0), 0);

                    const poorHealthBrands = resp.data.brands.filter(b => 
                        // (b.healthStatus && b.healthStatus !== 'Healthy' && b.healthStatus !== 'Active') || 
                        (b.sps !== 'N/A' && n(b.sps) < 3.5)
                    );

                    const expiringPromoBrands = resp.data.brands.filter(b => (b.expiringPromotionsCount || 0) > 0 || b.hasNoPromotions);
                    // Only count brands with genuinely expiring promos — hasNoPromotions is informational, not an action item
                    const totalExpiringAlerts = resp.data.brands.filter(b => (b.expiringPromotionsCount || 0) > 0).length;

                    const crmBrands = resp.data.brands.filter(b => (b.crmPlansCount || 0) > 0 || b.hasNoCrmPlans);
                    const totalCrmPlans = resp.data.brands.reduce((sum, b) => sum + (b.crmPlansCount || 0), 0);

                    setAlerts([
                        { 
                            id: 1, 
                            label: 'Policy Violations', 
                            count: totalViolations, 
                            items: violationBrands.map(b => `${b.name}: ${b.violations} Violation${b.violations > 1 ? 's' : ''}`),
                            type: 'danger'
                        },
                        { 
                            id: 2, 
                            label: 'New Campaigns Avail.', 
                            count: totalAvailableCampaigns, 
                            items: availableCampaignBrands.map(b => `${b.name}: ${b.availableCampaignsCount} Campaign${b.availableCampaignsCount > 1 ? 's' : ''}`),
                            type: 'info'
                        },
                        { 
                            id: 3, 
                            label: 'Shop Health', 
                            count: poorHealthBrands.length, 
                            items: poorHealthBrands.map(b => `${b.name}: Low SPS (${b.sps})`),
                            type: 'warning'
                        },
                        { 
                            id: 4, 
                            label: 'Low Inventory', 
                            count: totalLowStock, 
                            items: lowStockBrands.map(b => `${b.name}: ${b.lowStockCount} Item${b.lowStockCount > 1 ? 's' : ''} Low`),
                            type: 'inventory'
                        },
                        { 
                            id: 5, 
                            label: 'Promotions Expiring', 
                            count: totalExpiringAlerts, 
                            items: expiringPromoBrands.map(b => `${b.name}: ${b.hasNoPromotions ? 'No Active Promotions' : `${b.expiringPromotionsCount} Expiring`}`),
                            type: 'promo'
                        },
                        { 
                            id: 6, 
                            label: 'CRM Campaigns Completed', 
                            count: totalCrmPlans, 
                            items: crmBrands.map(b => `${b.name}: ${b.hasNoCrmPlans ? 'No plans' : `${b.crmPlansCount} Plan${b.crmPlansCount > 1 ? 's' : ''}`}`),
                            type: 'crm'
                        }
                    ]);
                }
            } catch (err) {
                console.error("Error fetching roster:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user, dateRange]);

    const getInsightIcon = (type) => {
        switch(type) {
            case 'performance': return <TrendingUp size={18} />;
            case 'inventory': return <Package size={18} />;
            case 'health': return <AlertTriangle size={18} />;
            case 'ads': return <Megaphone size={18} />;
            default: return <Zap size={18} />;
        }
    };

    const getInsightStyle = (type) => {
        if (isLight) {
            switch(type) {
                case 'performance': return { background: 'rgba(16, 185, 129, 0.22)', color: '#047857', boxShadow: '0 0 0 1.5px rgba(16,185,129,0.35)' };
                case 'inventory':   return { background: 'rgba(245, 158, 11, 0.26)', color: '#92400e', boxShadow: '0 0 0 1.5px rgba(245,158,11,0.38)' };
                case 'health':      return { background: 'rgba(239, 68, 68, 0.22)',  color: '#b91c1c', boxShadow: '0 0 0 1.5px rgba(239,68,68,0.35)' };
                default:            return { background: 'rgba(99, 102, 241, 0.22)', color: '#3730a3', boxShadow: '0 0 0 1.5px rgba(99,102,241,0.35)' };
            }
        }
        switch(type) {
            case 'performance': return { background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' };
            case 'inventory': return { background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' };
            case 'health': return { background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' };
            case 'ads': return { background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' };
            default: return { background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7' };
        }
    };

    return (
        <UserLayout>
            <div className="page-relative-wrapper">
                <div className="command-centre-container">
                    {/* Agency Intelligence Report */}
                    <div className={`intelligence-report-card ${loading ? 'shimmer skeleton' : ''}`}>
                        <div className="report-header">
                            <div className="report-title-group">
                                <div className="report-icon-box">
                                    <div className="inner-icon-bg">
                                        <Activity size={24} color="#3b82f6" />
                                    </div>
                                </div>
                                <div>
                                    <h2 className="report-main-title">Agency Intelligence Report</h2>
                                    <p className="report-subtitle">Real-time insights across all managed accounts</p>
                                </div>
                            </div>
                            <div className="live-badge">Live</div>
                        </div>

                        <div className="report-grid">
                            {(loading ? [1, 2, 3, 4] : intelligenceInsights).length > 0 ? (
                                (loading ? [1, 2, 3, 4] : intelligenceInsights).map((insight, idx) => (
                                    <div key={idx} className={`report-item type-${insight.type || 'performance'}`}>
                                        <div className="report-item-icon" style={getInsightStyle(insight.type || 'performance')}>
                                            {getInsightIcon(insight.type || 'performance')}
                                        </div>
                                        <div
                                            className="report-item-text"
                                            dangerouslySetInnerHTML={{ __html: insight.content || 'Generating intelligence insight for your portfolio...' }}
                                        />
                                    </div>
                                ))
                            ) : (
                                <div className="report-item">
                                    <div className="report-item-icon" style={{ background: 'rgba(156, 163, 175, 0.1)', color: '#9ca3af' }}>
                                        <Activity size={18} />
                                    </div>
                                    <div className="report-item-text">
                                        Collective data analysis complete. No critical anomalies detected across the portfolio.
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Agency Performance Aggregate */}
                    <div className="perf-aggregate-section-card">
                        <div className="section-title-alt">
                            <div className="perf-title-icon">
                                <LayoutGrid size={20} color="#10b981" fill="#10b981" />
                            </div>
                            Agency Performance Aggregate
                        </div>
                        <div className="performance-grid">
                            {/* GMV Card */}
                            <div className={`perf-card ${loading ? 'shimmer skeleton' : ''}`}>
                                <div className="perf-label">Total GMV Managed</div>
                                <div className="perf-main-value gmv-color">{performanceData.gmv.total}</div>
                                {/* <div className="perf-trend-container">
                                    <span className="trend-period">Last 30 Days</span>
                                    <span className="trend-value up">
                                        <ArrowUpRight size={14} /> {performanceData.gmv.growth}
                                    </span>
                                </div> */}
                                <div className="perf-separator"></div>
                                <div className="perf-details">
                                    <div className="perf-detail-item">
                                        <span className="detail-label">Brands</span>
                                        <span className="detail-value">{performanceData.gmv.activeBrands}</span>
                                    </div>
                                    <div className="perf-detail-item">
                                        <span className="detail-label">Top Brand <span className="gmv-color">{"(" + performanceData.gmv.topBrand + ")"}</span></span>
                                        <span className="detail-value gmv-color">{performanceData.gmv.topBrandVal}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Ad Spend Card */}
                            <div className={`perf-card ${loading ? 'shimmer skeleton' : ''}`}>
                                <div className="perf-label">Total Ad Spend</div>
                                <div className="perf-main-value spend-color">{performanceData.adSpend.total}</div>
                                {/* <div className="perf-trend-container">
                                    <span className="trend-period">Across All Brands</span>
                                    <span className="trend-value up-blue">
                                        <ArrowUpRight size={14} /> {performanceData.adSpend.growth}
                                    </span>
                                </div> */}
                                <div className="perf-separator"></div>
                                <div className="perf-details">
                                    <div className="perf-detail-item">
                                        <span className="detail-label">GMV / Ad Spend</span>
                                        <span className="detail-value">{performanceData.adSpend.avgRoi}</span>
                                    </div>
                                    <div className="perf-detail-item">
                                        <span className="detail-label">Total Portfolio GMV</span>
                                        <span className="detail-value spend-color">{performanceData.adSpend.totalRevenue}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Orders Card */}
                            <div className={`perf-card ${loading ? 'shimmer skeleton' : ''}`}>
                                <div className="perf-label">Total Orders</div>
                                <div className="perf-main-value orders-color">{performanceData.orders.total}</div>
                                {/* <div className="perf-trend-container">
                                    <span className="trend-period">This Month</span>
                                    <span className="trend-value up-orange">
                                        <ArrowUpRight size={14} /> {performanceData.orders.growth}
                                    </span>
                                </div> */}
                                <div className="perf-separator"></div>
                                <div className="perf-details">
                                    <div className="perf-detail-item">
                                        <span className="detail-label">Avg Order Value</span>
                                        <span className="detail-value">{performanceData.orders.aov}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Critical Alerts & Triage */}
                    <div className={`alerts-triage-section-card ${loading ? 'shimmer skeleton' : ''}`}>
                        <div className="section-title-alt">
                            <div className="alerts-title-icon-wrapper">
                                <Bell size={18} color="#ef4444" fill="#ef4444" />
                            </div>
                            Critical Alerts & Triage
                        </div>
                        <div className="alerts-list">
                            {alerts.map(alert => (
                                <div key={alert.id} className={`alert-triage-row alert-type-${alert.type}`}>
                                    <div className="alert-row-left">
                                        <div className="alert-row-title">{alert.label}</div>
                                        <div className="alert-row-subtitle">
                                            {alert.count > 0 ? `${alert.count} Critical Item${alert.count > 1 ? 's' : ''}` : 'Status Clear'}
                                        </div>
                                        <div className="alert-row-items">
                                            {alert.items.length > 0 ? alert.items.join(" • ") : "No critical items detected across the portfolio."}
                                        </div>
                                    </div>
                                    <div className="alert-row-right">
                                        <div className={`alert-pill-badge ${alert.count > 0 ? 'active' : 'clear'}`}>
                                            {alert.count > 0 ? 'ACTION REQ.' : 'HEALTHY'}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Master Brand Roster */}
                    <div className="brand-roster-section-card">
                        <div className="section-title-alt">
                            <div className="roster-title-icon">
                                <Layers size={20} color="#a855f7" fill="#a855f7" />
                            </div>
                            Master Brand Roster
                        </div>
                        <div className="brand-roster-grid">
                            {(loading ? [1, 2, 3, 4] : brands).map((brand, idx) => (
                                <div key={loading ? idx : brand.id} className={`brand-card ${loading ? 'shimmer skeleton' : ''}`}>
                                    <div className="brand-card-header">
                                        <div className="brand-info">
                                            <div className="brand-logo">
                                                <img src={brand.logo || ''} alt={brand.name || 'Shop'} />
                                            </div>
                                            <div>
                                                <div className="brand-name">{brand.name || 'Loading Shop...'}</div>
                                                <div className="brand-category">{brand.category || 'General'}</div>
                                            </div>
                                        </div>
                                        <div className={`brand-status-badge ${brand.status?.toLowerCase().replace(/\s+/g, '-') || 'inactive'}`}>
                                            <div className="status-dot-small" />
                                            {brand.status || 'Checking'}
                                        </div>
                                    </div>

                                    <div className="brand-metrics-summary">
                                        <div className="brand-metric-col">
                                            <span className="brand-metric-label">GMV</span>
                                            <span className="brand-metric-val gmv-color">{brand.gmv || '$0'}</span>
                                            <span className={`brand-metric-trend ${(brand.gmvGrowth || '').startsWith('-') ? 'down' : (brand.gmvGrowth || '').startsWith('+') ? 'up' : 'neutral'}`}>
                                                {(brand.gmvGrowth || '').startsWith('-') ? <TrendingDown size={12} /> : (brand.gmvGrowth || '').startsWith('+') ? <ArrowUpRight size={12} /> : null}
                                                {' '}{brand.gmvGrowth?.replace('+', '').replace('-', '') || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="brand-metric-col">
                                            <span className="brand-metric-label">Orders</span>
                                            <span className="brand-metric-val">{brand.orders || '0'}</span>
                                            <span className={`brand-metric-trend ${(brand.ordersGrowth || '').startsWith('-') ? 'down' : (brand.ordersGrowth || '').startsWith('+') ? 'up' : 'neutral'}`}>
                                                {(brand.ordersGrowth || '').startsWith('-') ? <TrendingDown size={12} /> : (brand.ordersGrowth || '').startsWith('+') ? <ArrowUpRight size={12} /> : null}
                                                {' '}{brand.ordersGrowth?.replace('+', '').replace('-', '') || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="brand-metric-col">
                                            <span className="brand-metric-label">SPS</span>
                                            <span className="brand-metric-val sps-color">{brand.sps || 'N/A'}</span>
                                            <span className="brand-metric-level">{brand.spsStatus || 'Normal'}</span>
                                        </div>
                                        <div className="brand-metric-col">
                                            <span className="brand-metric-label">Shop Visitors</span>
                                            <span className="brand-metric-val">{brand.visitors || '0'}</span>
                                        </div>
                                    </div>

                                    <div className="brand-card-separator"></div>

                                    <div className="brand-details-grid">
                                        <div className="brand-details-list">
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">Ad Spend</span>
                                                <span className="brand-detail-val">{brand.adSpend || '$0'}</span>
                                            </div>
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">Violations</span>
                                                <span className="brand-detail-val">{brand.violations || '0'}</span>
                                            </div>
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">Samples Pending</span>
                                                <span className="brand-detail-val">{brand.samplesPending || '0'}</span>
                                            </div>
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">Samples Approved</span>
                                                <span className="brand-detail-val">{brand.samplesApproved || '0'}</span>
                                            </div>
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">Gross Profit</span>
                                                <span className="brand-detail-val">{brand.grossProfit || '$0'}</span>
                                            </div>
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">Campaigns Enrolled</span>
                                                <span className="brand-detail-val">{brand.campaignsEnrolled || '0'}</span>
                                            </div>
                                        </div>
                                        <div className="brand-details-list">
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">ROI</span>
                                                <span className="brand-detail-val">{brand.roi || '0x'}</span>
                                            </div>
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">Cost/Order</span>
                                                <span className="brand-detail-val">{brand.costPerOrder || '$0'}</span>
                                            </div>
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">Shipped</span>
                                                <span className="brand-detail-val">{brand.shipped || '0'}</span>
                                            </div>
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">GMV Max Revenue</span>
                                                <span className="brand-detail-val">{brand.gmvMaxRevenue || '$0'}</span>
                                            </div>
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">Campaign Running</span>
                                                <span className={`brand-detail-val ${brand.campaignRunning?.toLowerCase() === 'active' ? 'active-status' : 'inactive-status'}`}>
                                                    {brand.campaignRunning || 'Checking'}
                                                </span>
                                            </div>
                                            <div className="brand-detail-row">
                                                <span className="brand-detail-label">Promotion</span>
                                                <span className={`brand-detail-val ${brand.promotion?.toLowerCase() === 'active' ? 'active-status' : 'inactive-status'}`}>
                                                    {brand.promotion || 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </UserLayout>
    );
};

export default CommandCentrePage;
